/**
 * TrackListView — one list of tracks: the first page on entry, the next as the scroll nears the
 * end, «Ещё раз» when a page does not come, and a tap that plays with the list as the queue.
 *
 * Task 05 wrote it inside the «Треки» tab (`screens/Tracks.tsx`); task 11 moved it here, because
 * «Моё» shows its favourites and recent tracks the same way — the server answers both in the
 * page shape of `/api/tracks` (`apps/api/src/routes/my.ts`), and `store/library.ts` keeps them
 * under their own keys beside the catalogue's lists.
 *
 * ## Paging follows the scroll
 *
 * The first page of a list arrives on entry; the next one is asked for when the virtual
 * list's window comes within {@link LOAD_AHEAD_ROWS} rows of the end. Scrolling therefore
 * never waits at the bottom of a page on a normal connection, and a list nobody scrolls costs
 * one request.
 */

import type { JSX, ReactNode } from 'react';
import { useCallback, useEffect, useMemo } from 'react';

import { player } from '../engine/player';
import type { TrackItem, TrackQuery } from '../lib/api';
import { listKey, useLibrary } from '../store/library';

import { NeonSign } from './NeonSign';
import type { NeonKind } from './NeonSign';
import { ROW_HEIGHT, TrackRow } from './TrackRow';
import { VirtualList } from './VirtualList';
import type { VisibleRange } from './VirtualList';

export const RETRY_LABEL = 'Ещё раз';
export const MORE_LOADING_TEXT = 'Загружаю ещё…';
export const MORE_FAILED_TEXT = 'Следующие треки не загрузились.';

/** Ask for the next page when the window is this close to the last loaded row. */
export const LOAD_AHEAD_ROWS = 30;

type TrackListRow =
  | {
      readonly key: string;
      readonly height: number;
      readonly kind: 'track';
      readonly track: TrackItem;
    }
  | { readonly key: string; readonly height: number; readonly kind: 'more' | 'more-failed' };

/**
 * A list's whole place, when it has no rows to show: a sentence, and «Ещё раз» when asking again
 * may help.
 *
 * Task 68: `sign` puts the neon sign over the sentence (`components/NeonSign.tsx`; until then
 * the dimmed house logo, task 41). It means «something will be here» — an empty search, no
 * favourites yet — and is never drawn over a load, a failure or «nothing found»: there it would
 * read as «all is well» over a sentence that says otherwise. `children` go under the sentence —
 * «Смотреть все треки» under an empty «Новое».
 */
export function ListState({
  text,
  onRetry,
  sign,
  children,
}: {
  readonly text: string;
  readonly onRetry?: (() => void) | undefined;
  readonly sign?: NeonKind | undefined;
  readonly children?: ReactNode;
}): JSX.Element {
  return (
    <div className={sign === undefined ? 'listState' : 'listState listStateSign'}>
      {sign !== undefined && <NeonSign kind={sign} />}
      <p className="stateText">{text}</p>
      {onRetry !== undefined && (
        <div className="actions">
          <button type="button" className="button" onClick={onRetry}>
            {RETRY_LABEL}
          </button>
        </div>
      )}
      {children}
    </div>
  );
}

/**
 * Plays `track` with the queue built from the list under `key`, from `track` down (task 08) —
 * read from the store at the moment of the tap, so a caller's callback keeps one identity
 * while pages arrive. The rows loaded so far are the list: a queue does not wait for pages the
 * listener has not scrolled to. Task 44: it takes the list's next page along as its source, so
 * the player goes on down the list as the queue nears its end.
 */
export function playIn(key: string, track: TrackItem): void {
  const list = useLibrary.getState().lists[key];
  const cursor = list?.nextCursor ?? null;
  player().playFrom(list?.items ?? [track], track, cursor === null ? null : { key, cursor });
}

/**
 * One list of tracks. `query` must keep its identity for as long as it means the same list:
 * it is an effect dependency, and a fresh object per render would re-run the load on every
 * scroll. The screens keep theirs in module constants or memoise them.
 */
export function TrackListView({
  query,
  label,
  emptyText,
  loadingText,
  maxAgeMs,
  onOpenTrack,
  emptySign,
  emptyAction,
  onPick,
  pickedId,
}: {
  readonly query: TrackQuery;
  readonly label: string;
  readonly emptyText: string;
  /** What the list says while its first page is on the way. */
  readonly loadingText: string;
  /**
   * A list older than this is fetched again when the view appears, and the rows already there
   * stay on screen until the new ones replace them (`store/library.ts`).
   */
  readonly maxAgeMs: number;
  readonly onOpenTrack: (track: TrackItem) => void;
  /** Task 68: the empty list is a place that will fill — this sign over `emptyText`. */
  readonly emptySign?: NeonKind | undefined;
  /** Task 68: what goes under `emptyText` of an empty list — a way somewhere else. */
  readonly emptyAction?: ReactNode;
  /**
   * Task 54: a tap on a row chooses the track instead of playing it — the pult's screen LIBRARY,
   * choosing for LOAD — and `pickedId` is the row chosen, lit. Absent, a tap plays.
   */
  readonly onPick?: ((track: TrackItem) => void) | undefined;
  readonly pickedId?: number | undefined;
}): JSX.Element {
  const key = listKey(query);
  const list = useLibrary((s) => s.lists[key]);
  const loadList = useLibrary((s) => s.loadList);
  const loadMore = useLibrary((s) => s.loadMore);

  useEffect(() => {
    void loadList(query, { maxAgeMs });
  }, [query, loadList, maxAgeMs]);

  const items = list?.items;
  const rows = useMemo<TrackListRow[]>(() => {
    const out: TrackListRow[] = (items ?? []).map((track) => ({
      key: `t${String(track.id)}`,
      height: ROW_HEIGHT,
      kind: 'track',
      track,
    }));
    // One more row under the tracks while the list is not complete: it says the next page is
    // coming, or that it did not come and offers to try again.
    if (list?.moreFailed === true) {
      out.push({ key: 'more', height: ROW_HEIGHT, kind: 'more-failed' });
    } else if (list?.nextCursor !== null && list?.nextCursor !== undefined) {
      out.push({ key: 'more', height: ROW_HEIGHT, kind: 'more' });
    }
    return out;
  }, [items, list?.moreFailed, list?.nextCursor]);

  /*
   * The tap on a row plays it, and builds the queue from the list it was tapped in, from that
   * row down (task 08). Read from the store at the moment of the tap rather than closed over,
   * so the callback keeps one identity while pages arrive and the memoised rows do not
   * re-render for it.
   */
  const playFromList = useCallback(
    (track: TrackItem): void => {
      playIn(key, track);
    },
    [key],
  );

  const itemCount = items?.length ?? 0;
  const onRangeChange = useCallback(
    (range: VisibleRange): void => {
      if (range.end >= itemCount - LOAD_AHEAD_ROWS) void loadMore(query);
    },
    [itemCount, query, loadMore],
  );

  const renderRow = useCallback(
    (row: TrackListRow): JSX.Element => {
      if (row.kind === 'track') {
        return (
          <TrackRow
            track={row.track}
            onPlay={onPick ?? playFromList}
            onDetails={onOpenTrack}
            picked={pickedId === row.track.id}
          />
        );
      }
      if (row.kind === 'more') {
        return (
          <li className="row">
            <div className="rowNote">{MORE_LOADING_TEXT}</div>
          </li>
        );
      }
      return (
        <li className="row">
          <div className="rowNote">
            {MORE_FAILED_TEXT}
            <button
              type="button"
              className="linkButton"
              onClick={() => {
                void loadMore(query, { retry: true });
              }}
            >
              {RETRY_LABEL}
            </button>
          </div>
        </li>
      );
    },
    [onOpenTrack, onPick, pickedId, playFromList, query, loadMore],
  );

  if (list === undefined || list.status === 'loading' || list.status === 'idle') {
    return <ListState text={loadingText} />;
  }
  if (list.status === 'failed') {
    return (
      <ListState
        text={list.failure?.message ?? loadingText}
        onRetry={() => {
          void loadList(query);
        }}
      />
    );
  }
  if (list.items.length === 0) {
    return (
      <ListState text={emptyText} sign={emptySign}>
        {emptyAction}
      </ListState>
    );
  }

  return (
    <VirtualList
      rows={rows}
      renderRow={renderRow}
      onRangeChange={onRangeChange}
      as="ul"
      ariaLabel={label}
    />
  );
}
